import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';

import '../contracts/Cofre_StoreInput';
import '../contracts/Cofre_StoreResponse';
import '../contracts/Cofre_UpdateInput';
import '../contracts/Cofre_UpdateResponse';

/**
 * Composable para CRUD de contas Cofre.
 * Fornece métodos para listar, criar, editar e excluir contas bancárias.
 */
export function useCofre() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const contas = ref<any[]>([]);
  const saldoTotal = ref<number>(0);

  /**
   * Lista todas as contas da franquia.
   */
  const fetchContas = async (franquiaId: number) => {
    log('useCofre', 'fetchContas', 'Carregando contas', { franquiaId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<any>(
        `/index.php/cofre?franquia_id=${franquiaId}`,
        { responseContract: 'Cofre_IndexResponse' }
      );

      if (response.success) {
        contas.value = response.dados.items || [];
        saldoTotal.value = contas.value.reduce(
          (sum: number, c: any) => sum + (parseFloat(c.saldo_atual) || 0), 0
        );
        log('useCofre', 'fetchContas', 'Contas carregadas', { total: contas.value.length });
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao carregar contas');
    } catch (e: any) {
      error.value = e.message;
      log('useCofre', 'fetchContas', 'Erro', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Busca uma conta pelo ID.
   */
  const fetchConta = async (id: number) => {
    log('useCofre', 'fetchConta', 'Buscando conta', { id });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<any>(
        `/index.php/cofre/show/${id}`,
        { responseContract: 'Cofre_ShowResponse' }
      );

      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao buscar conta');
    } catch (e: any) {
      error.value = e.message;
      log('useCofre', 'fetchConta', 'Erro', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Busca o extrato de sangrias de uma conta do cofre.
   */
  const fetchExtrato = async (contaId: number) => {
    log('useCofre', 'fetchExtrato', 'Buscando extrato', { contaId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<any>(
        `/index.php/cofre/extrato/${contaId}`,
        { responseContract: 'Cofre_ExtratoResponse' }
      );

      if (response.success) {
        log('useCofre', 'fetchExtrato', 'Extrato carregado', { totalItems: response.dados?.items?.length || 0 });
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao carregar extrato');
    } catch (e: any) {
      error.value = e.message;
      log('useCofre', 'fetchExtrato', 'Erro', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cria uma nova conta.
   */
  const criarConta = async (data: {
    franquia_id: number;
    nome: string;
    banco?: string;
    agencia?: string;
    conta?: string;
    tipo?: string;
    saldo_atual?: number;
    observacao?: string;
  }) => {
    log('useCofre', 'criarConta', 'Criando conta', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<any>(
        '/index.php/cofre/store',
        data,
        { inputContract: 'Cofre_StoreInput', responseContract: 'Cofre_StoreResponse' }
      );

      if (response.success) {
        log('useCofre', 'criarConta', 'Conta criada', response.dados);
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao criar conta');
    } catch (e: any) {
      error.value = e.message;
      log('useCofre', 'criarConta', 'Erro', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Atualiza uma conta existente.
   */
  const atualizarConta = async (id: number, data: Record<string, any>) => {
    log('useCofre', 'atualizarConta', 'Atualizando conta', { id, data });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.put<any>(
        `/index.php/cofre/update/${id}`,
        data,
        { inputContract: 'Cofre_UpdateInput', responseContract: 'Cofre_UpdateResponse' }
      );

      if (response.success) {
        log('useCofre', 'atualizarConta', 'Conta atualizada');
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao atualizar conta');
    } catch (e: any) {
      error.value = e.message;
      log('useCofre', 'atualizarConta', 'Erro', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Exclui (soft delete) uma conta.
   */
  const excluirConta = async (id: number) => {
    log('useCofre', 'excluirConta', 'Excluindo conta', { id });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.delete<any>(
        `/index.php/cofre/destroy/${id}`,
        { responseContract: 'Cofre_StoreResponse' }
      );

      if (response.success) {
        log('useCofre', 'excluirConta', 'Conta excluída');
        return true;
      }
      throw new Error(response.error || 'Erro ao excluir conta');
    } catch (e: any) {
      error.value = e.message;
      log('useCofre', 'excluirConta', 'Erro', e);
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    contas,
    saldoTotal,
    fetchContas,
    fetchConta,
    fetchExtrato,
    criarConta,
    atualizarConta,
    excluirConta,
  };
}
